/**
 * Typed wrappers over `http.*` for the contacts CRM-engine backend routes.
 *
 * The contacts module issued these as raw `fetch()` calls built on
 * `env.apiBaseUrl` + a per-file static-Bearer header helper (`getHeaders()` /
 * `authHeaders()`), which bypasses the http client's token rotation and 401
 * single-flight refresh-retry (#3268). Re-expressing each route as a thin
 * async function over `http.*` lets the call sites drop their raw network
 * layer and duplicated header helpers, and gains the refresh-retry for free.
 *
 * Conventions (mirroring endpoints/email-module.ts):
 *   - every fn takes an explicit `authToken?: string | null` threaded into the
 *     http options (callers control token rotation / unauth requests; when
 *     omitted the http client reads the stored access token itself, exactly
 *     the `getAuthItem('xavior_auth_token')` the raw calls used),
 *   - request + response shapes are hand-typed: `xavior-openapi.yaml` has ZERO
 *     `/crm-engine/*` paths, so there are no generated contract types — the
 *     hand-typed precedent is endpoints/email-module.ts,
 *   - each fn returns `response.data` (the parsed JSON body).
 *
 * The `Authorization`, `x-user-id` and `x-workspace-id` headers the raw calls
 * sent are all supplied by the http client (it derives `x-user-id` from the
 * token payload, falling back to the same stored `userId`), so the wire
 * requests stay equivalent.
 */
import { http } from '@/lib/http'
import { getActiveWorkspaceId } from '../workspace-context'

/** `/workspaces/{ws}/crm-engine` — matches the raw call sites' base path. */
function crmEngineBasePath(): string {
  return `/workspaces/${getActiveWorkspaceId()}/crm-engine`
}

/* ------------------------------------------------------------------ *
 * Shapes (hand-typed — no OpenAPI schema for /crm-engine/*)
 * ------------------------------------------------------------------ */

/** A pre-computed AI draft response for a contact (mirrors AIResponsePanel). */
export interface ContactDraft {
  id: string
  contactId: string
  actionType: string
  subject: string
  body: string
  reasoning?: string
  snippetsUsed: string[]
  confidence: number
  tone: string
  status: string
  generatedAt: string
}

export interface ContactDraftsResponse {
  drafts?: ContactDraft[]
}

/** A toned email-draft variant (mirrors DraftVariantSelector's DraftVariant). */
export interface DraftVariantWire {
  tone: string
  subject: string
  body: string
}

export interface DraftEmailResponse {
  variants?: DraftVariantWire[]
}

/**
 * The relationship-state object is consumed by the contacts module's own
 * `RelationshipState` type; left as `unknown` here so the endpoints layer does
 * not depend on `modules/` (one-way import direction). Callers narrow it.
 */
export interface RelationshipStateResponse {
  state?: unknown
}

export interface AnalyzeContactResponse {
  relationshipState?: unknown
}

/** One contact's score row in the batch relationship-states response. */
export interface RelationshipScoreWire {
  followUpScore: number
  scoreBand: string
  relationshipState: string
  [key: string]: unknown
}

export interface RelationshipStatesResponse {
  states?: Record<string, RelationshipScoreWire>
}

/* ------------------------------------------------------------------ *
 * Drafts
 * ------------------------------------------------------------------ */

/** GET `/crm-engine/drafts/{contactId}` — pre-computed drafts for a contact. */
export async function getContactDrafts(
  contactId: string,
  authToken?: string | null,
): Promise<ContactDraftsResponse> {
  const res = await http.get<ContactDraftsResponse>(
    `${crmEngineBasePath()}/drafts/${contactId}`,
    { authToken },
  )
  return res.data
}

/** POST `/crm-engine/generate-drafts` — generate fresh drafts for a contact. */
export async function generateContactDrafts(
  contactId: string,
  authToken?: string | null,
): Promise<void> {
  await http.post(`${crmEngineBasePath()}/generate-drafts`, { contactId }, { authToken })
}

/** POST `/crm-engine/drafts/{draftId}/use` — mark a draft as used. */
export async function markDraftUsed(
  draftId: string,
  authToken?: string | null,
): Promise<void> {
  await http.post(`${crmEngineBasePath()}/drafts/${draftId}/use`, undefined, { authToken })
}

/** POST `/crm-engine/drafts/{draftId}/reject` — reject a draft. */
export async function rejectDraft(
  draftId: string,
  authToken?: string | null,
): Promise<void> {
  await http.post(`${crmEngineBasePath()}/drafts/${draftId}/reject`, undefined, { authToken })
}

/* ------------------------------------------------------------------ *
 * Relationship state
 * ------------------------------------------------------------------ */

/** GET `/crm-engine/relationship-state/{contactId}` — current relationship state. */
export async function getRelationshipState(
  contactId: string,
  authToken?: string | null,
): Promise<RelationshipStateResponse> {
  const res = await http.get<RelationshipStateResponse>(
    `${crmEngineBasePath()}/relationship-state/${contactId}`,
    { authToken },
  )
  return res.data
}

/** POST `/crm-engine/analyze-contact` — (re)compute a contact's relationship state. */
export async function analyzeContact(
  contactId: string,
  authToken?: string | null,
): Promise<AnalyzeContactResponse> {
  const res = await http.post<AnalyzeContactResponse>(
    `${crmEngineBasePath()}/analyze-contact`,
    { contactId },
    { authToken },
  )
  return res.data
}

/** POST `/crm-engine/draft-email` — draft email variants for a relationship action. */
export async function draftEmail(
  contactId: string,
  actionType: string,
  authToken?: string | null,
): Promise<DraftEmailResponse> {
  const res = await http.post<DraftEmailResponse>(
    `${crmEngineBasePath()}/draft-email`,
    { contactId, actionType },
    { authToken },
  )
  return res.data
}

/** GET `/crm-engine/relationship-states?contactIds=…` — batch score lookup. */
export async function getRelationshipStates(
  contactIds: string[],
  authToken?: string | null,
): Promise<RelationshipStatesResponse> {
  const res = await http.get<RelationshipStatesResponse>(
    `${crmEngineBasePath()}/relationship-states?contactIds=${contactIds.join(',')}`,
    { authToken },
  )
  return res.data
}
